function optionIds(value, field) {
  const options = Array.isArray(value?.[field]) ? value[field] : [];
  return options
    .map(option => option?.optionID ?? option?.id)
    .filter(optionId => typeof optionId === 'string' && optionId.length > 0)
    .sort();
}

function lineKey(line, optionField) {
  const productId = line?.productID ?? line?.productId;
  if (typeof productId !== 'string' || productId.length === 0) return null;
  return JSON.stringify([productId, optionIds(line, optionField)]);
}

/**
 * Remove only quantities proven to belong to a materialized order.
 *
 * A shopper can add more items in another tab while Stripe is redirecting.
 * Clearing the whole cart would discard those later additions. This multiset
 * subtraction consumes the ordered quantity for each product/modifier
 * configuration and preserves every unrelated or excess quantity.
 */
export function subtractPurchasedItems(cartItems, purchasedItems) {
  if (!Array.isArray(cartItems)) return [];
  if (!Array.isArray(purchasedItems) || purchasedItems.length === 0) {
    return [...cartItems];
  }

  const remainingByKey = new Map();
  purchasedItems.forEach(line => {
    const key = lineKey(line, 'selectedOptions');
    const quantity = line?.quantity;
    if (!key || !Number.isSafeInteger(quantity) || quantity <= 0) return;
    remainingByKey.set(key, (remainingByKey.get(key) || 0) + quantity);
  });

  const result = [];
  cartItems.forEach(item => {
    const key = lineKey(item, 'selectedModifiers');
    const quantity = item?.quantity;
    if (!key || !Number.isSafeInteger(quantity) || quantity <= 0) {
      result.push(item);
      return;
    }
    const purchased = remainingByKey.get(key) || 0;
    if (purchased <= 0) {
      result.push(item);
      return;
    }
    const consumed = Math.min(quantity, purchased);
    remainingByKey.set(key, purchased - consumed);
    if (consumed < quantity) {
      result.push({ ...item, quantity: quantity - consumed });
    }
  });
  return result;
}
